"use client";

import * as React from "react";
import { ShoppingCart } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";

type BasketBadgeProps = {
  itemCount: number;
  onClick?: () => void;
  className?: string;
  variant?: "default" | "floating";
};

/**
 * Geometry of the floating BasketBadge, mirrored from the Tailwind classes in
 * the `variant="floating"` branch below (`bottom-4` = 16px from the viewport
 * bottom, `h-14` = 56px tall). Exported so anything that stacks UI above the
 * badge — e.g. the website chat bubble — derives its offset from one place
 * instead of hard-coding a number that silently drifts when these classes
 * change. Keep in sync with the classNames below.
 */
export const FLOATING_BASKET_BADGE_GEOMETRY = {
  bottomPx: 16,
  sizePx: 56,
} as const;

function BasketBadge({
  itemCount,
  onClick,
  className,
  variant = "default",
}: BasketBadgeProps) {
  if (variant === "floating") {
    return (
      <Button
        variant="default"
        size="icon"
        onClick={onClick}
        className={cn(
          "fixed bottom-4 right-4 z-40 h-14 w-14 rounded-full shadow-lg",
          "bg-primary text-primary-foreground hover:bg-primary/90",
          className
        )}
        aria-label={`View basket with ${itemCount} items`}
      >
        <div className="relative">
          <ShoppingCart className="h-6 w-6" />
          {itemCount > 0 && (
            <span
              className={cn(
                "absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center",
                "rounded-full bg-destructive text-destructive-foreground",
                "text-xs font-bold"
              )}
            >
              {itemCount > 99 ? "99+" : itemCount}
            </span>
          )}
        </div>
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className={cn("relative", className)}
      aria-label={`View basket with ${itemCount} items`}
    >
      <ShoppingCart className="h-5 w-5" />
      {itemCount > 0 && (
        <span
          className={cn(
            "absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center",
            "rounded-full bg-primary text-primary-foreground",
            "text-xs font-bold"
          )}
        >
          {itemCount > 99 ? "99+" : itemCount}
        </span>
      )}
    </Button>
  );
}

export { BasketBadge };
export type { BasketBadgeProps };
